import { ORDER_PLATFORM } from '../../../utils/Constants';
import { WAITER_ORIGIN } from '../../../utils/runtimeEnv';

/**
 * Origin of the app that framed us, taken from the `parent_origin` query param
 * the waiter PWA puts in the iframe URL.
 *
 * Captured at module load, not read per call: the SPA rewrites the URL as the
 * user moves through /qr-order, so the param is only reliably present on entry.
 *
 * Preferred over WAITER_ORIGIN because that value is derived from this app's own
 * env and hostname — a guess, and wrong whenever the two apps are served from
 * different hostnames. postMessage demands an exact target match and drops
 * mismatches silently, so a guess fails invisibly.
 */
const FRAMED_BY_ORIGIN = (() => {
  try {
    const value = new URLSearchParams(window.location.search).get('parent_origin');
    return value ? new URL(value).origin : null;
  } catch {
    return null;
  }
})();

export const QR_NATIVE_ACTIONS = {
  STOP_LOADER: 'stop-loader',
  REPRINT: 'reprint',
  GO_TO: 'go-to',
};

/**
 * Delivers a payload to the host waiter app.
 *
 * Native: react-native-webview injects window.ReactNativeWebView.
 * Web: the waiter PWA embeds this page in an iframe, so the host is the parent
 *
 * Both are attempted: exactly one exists in any given context.
 */
export const postAppMessage = (payload) => {
  const message = JSON.stringify(payload);

  window.ReactNativeWebView?.postMessage(message);

  const hostOrigin = FRAMED_BY_ORIGIN || WAITER_ORIGIN;

  if (hostOrigin && window.parent && window.parent !== window) {
    window.parent.postMessage(message, hostOrigin);
  }
};
export const WAITER_SELECT_TABLE_SCREEN = 'TakeOrderSelectTable';

export const postQrNativeMessage = (base, action, data = {}) => {
  if (base !== ORDER_PLATFORM.APP) {
    return;
  }

  postAppMessage({ action, data });
};

export const stopQrNativeLoader = (base) => {
  postQrNativeMessage(base, QR_NATIVE_ACTIONS.STOP_LOADER);
};

/**
 * Applies the non-zooming viewport when the page is embedded (`embed=1`).
 *
 * The waiter app used to inject this from the native side via WebView
 * injectedJavaScript. On web it embeds the page in a cross-origin iframe, where
 * script injection is impossible — so the page applies it to itself instead.
 * Content matches the script it replaces.
 *
 * Returns whether it applied, for tests.
 */
export const applyEmbeddedViewport = (search) => {
  const query = search !== undefined ? search : window.location.search;

  if (new URLSearchParams(query).get('embed') !== '1') {
    return false;
  }

  const meta = document.createElement('meta');
  meta.name = 'viewport';
  meta.content =
    'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no';
  document.getElementsByTagName('head')[0].appendChild(meta);

  return true;
};
